import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Zoom,
} from "@mui/material";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { pagePrimaryButtonSx } from "../../styles/pageModernStyles";
import { peekMoodboardAssetBlobUrl } from "./moodboardAssetBlobCache";
import { boardAiCanSubmit, normalizeBoardAiMode } from "./moodboardBoardAi";

const sparkle = keyframes`
  0%, 100% { transform: rotate(0deg) scale(1); }
  40% { transform: rotate(-8deg) scale(1.08); }
  70% { transform: rotate(6deg) scale(1.04); }
`;

const ENGINES = [
  { id: "gpt-image", label: "GPT Image" },
  { id: "nano-banana", label: "Nano Banana" },
  { id: "flux", label: "FLUX (necesita refs)" },
];

const SKETCH_VIEWS = [
  { id: "front", label: "Frontal" },
  { id: "back", label: "Trasera" },
  { id: "both", label: "Frontal + trasera" },
];

function RefThumb({ assetId, title, checked, disabled, onToggle, fetchBlob }) {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const [src, setSrc] = useState(() => peekMoodboardAssetBlobUrl(assetId));

  useEffect(() => {
    let cancelled = false;
    const peeked = peekMoodboardAssetBlobUrl(assetId);
    if (peeked) setSrc(peeked);
    (async () => {
      try {
        if (typeof fetchBlob !== "function") return;
        const url = await fetchBlob(assetId);
        if (!cancelled) setSrc(url || "");
      } catch {
        if (!cancelled) setSrc("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assetId, fetchBlob]);

  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onToggle}
      sx={{
        appearance: "none",
        border: "2px solid",
        borderColor: checked ? accent : alpha(accent, 0.16),
        bgcolor: checked ? alpha(accent, 0.08) : alpha("#0f172a", 0.03),
        borderRadius: 2,
        p: 0.75,
        width: 104,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled && !checked ? 0.45 : 1,
        textAlign: "left",
        transition:
          "border-color 160ms ease, background-color 160ms ease, transform 160ms ease",
        "&:hover": disabled
          ? undefined
          : {
              borderColor: accent,
              transform: "translateY(-1px)",
            },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          pt: "100%",
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: "#0b1220",
          mb: 0.6,
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
            }}
          >
            <CircularProgress size={16} sx={{ color: alpha("#fff", 0.5) }} />
          </Box>
        )}
        {checked ? (
          <Box
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              width: 22,
              height: 22,
              borderRadius: "50%",
              bgcolor: accent,
              color: "#fff",
              display: "grid",
              placeItems: "center",
              boxShadow: `0 2px 8px ${alpha(accent, 0.45)}`,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 16 }} />
          </Box>
        ) : null}
      </Box>
      <Typography
        variant="caption"
        noWrap
        title={title}
        sx={{
          display: "block",
          fontWeight: checked ? 800 : 600,
          color: checked ? accent : "text.primary",
          fontSize: "0.68rem",
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}

/**
 * Genera imagen con Prompt / Foto / Sketch e inyecta en tablero / página / pool.
 * Las piezas seleccionadas del board llegan como refs (máx. 4).
 */
export default function MoodboardGeneratePanel({
  open,
  onClose,
  busy = false,
  assets = [],
  selectedAssetIds = [],
  fetchBlob,
  onGenerate,
}) {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const paperBg =
    theme.palette.mode === "dark" ? theme.palette.background.paper : "#ffffff";
  const [mode, setMode] = useState("prompt");
  const [prompt, setPrompt] = useState("");
  const [engine, setEngine] = useState("gpt-image");
  const [place, setPlace] = useState("pasteboard");
  const [sketchView, setSketchView] = useState("front");
  const [refIds, setRefIds] = useState(() => new Set(selectedAssetIds));

  useEffect(() => {
    if (!open) return;
    setRefIds(
      new Set(
        (selectedAssetIds || [])
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id))
          .slice(0, 4),
      ),
    );
  }, [open, selectedAssetIds]);

  const assetOptions = useMemo(
    () => (assets || []).filter((a) => a?.id != null).slice(0, 40),
    [assets],
  );

  const toggleRef = (id) => {
    setRefIds((prev) => {
      const next = new Set(prev);
      const n = Number(id);
      if (next.has(n)) next.delete(n);
      else if (next.size < 4) next.add(n);
      return next;
    });
  };

  const canSubmit = boardAiCanSubmit({
    mode,
    prompt,
    engine,
    refCount: refIds.size,
  });

  const modeTitle =
    mode === "sketch"
      ? "Generar Sketch"
      : mode === "photo"
        ? "Generar foto (Diseños AI)"
        : "Generar con prompt";

  const modeLead =
    mode === "sketch"
      ? "Convierte las refs del board en un flat técnico. El resultado se inyecta en el collage."
      : mode === "photo"
        ? "Prompt + referencias del board con el motor de Diseños AI. El resultado vuelve al board."
        : "Crea una imagen con el motor del moodboard. Ancla hasta 4 piezas seleccionadas como referencia.";

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Zoom}
      transitionDuration={{ enter: 280, exit: 180 }}
      BackdropProps={{
        sx: {
          bgcolor: alpha("#0f172a", 0.5),
          backdropFilter: "blur(8px)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          overflow: "hidden",
          border: "1px solid",
          borderColor: alpha(accent, 0.22),
          bgcolor: paperBg,
          backgroundImage: "none",
          boxShadow: `0 24px 64px ${alpha(accent, 0.18)}, 0 8px 24px rgba(15,23,42,0.12)`,
        },
      }}
    >
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${accent}, ${alpha("#f59e0b", 0.85)})`,
        }}
      />
      <DialogContent
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1.5,
          backgroundImage: `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, ${paperBg} 42%)`,
        }}
      >
        <Stack spacing={2} sx={{ pt: 0.25 }}>
          <Stack direction="row" spacing={1.75} alignItems="flex-start">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                bgcolor: alpha(accent, 0.12),
                color: accent,
                border: "1px solid",
                borderColor: alpha(accent, 0.22),
                animation: open
                  ? `${sparkle} 2.8s ease-in-out infinite`
                  : "none",
              }}
            >
              {mode === "sketch" ? (
                <BrushOutlinedIcon sx={{ fontSize: 28 }} />
              ) : mode === "photo" ? (
                <PhotoCameraOutlinedIcon sx={{ fontSize: 28 }} />
              ) : (
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 28 }} />
              )}
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.2 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.45,
                  textTransform: "uppercase",
                  color: accent,
                }}
              >
                Board · IA
              </Typography>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ lineHeight: 1.25 }}
              >
                {modeTitle}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {modeLead}
              </Typography>
            </Box>
          </Stack>

          <ToggleButtonGroup
            exclusive
            size="small"
            fullWidth
            value={mode}
            onChange={(_, v) => v && setMode(normalizeBoardAiMode(v))}
            disabled={busy}
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 700,
                py: 0.85,
              },
            }}
          >
            <ToggleButton value="prompt">Prompt</ToggleButton>
            <ToggleButton value="photo">Foto</ToggleButton>
            <ToggleButton value="sketch">Sketch</ToggleButton>
          </ToggleButtonGroup>

          {mode !== "sketch" ? (
            <TextField
              label="Prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              placeholder="Bomber oversized en lana verde bosque, fondo blanco estudio…"
              disabled={busy}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: alpha(accent, 0.03),
                },
              }}
            />
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel id="mb-gen-sketch-view">Vista sketch</InputLabel>
              <Select
                labelId="mb-gen-sketch-view"
                label="Vista sketch"
                value={sketchView}
                onChange={(e) => setSketchView(e.target.value)}
                disabled={busy}
                sx={{ borderRadius: 2 }}
              >
                {SKETCH_VIEWS.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth size="small">
            <InputLabel id="mb-gen-engine">Motor</InputLabel>
            <Select
              labelId="mb-gen-engine"
              label="Motor"
              value={engine}
              onChange={(e) => setEngine(e.target.value)}
              disabled={busy}
              sx={{ borderRadius: 2 }}
            >
              {ENGINES.filter((e) =>
                mode === "sketch" ? e.id !== "nano-banana" : true,
              ).map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {e.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={800}
              letterSpacing={0.3}
            >
              DÓNDE COLOCAR EL RESULTADO
            </Typography>
            <RadioGroup
              row
              value={place}
              onChange={(e) => setPlace(e.target.value)}
              sx={{ mt: 0.5 }}
            >
              <FormControlLabel
                value="pasteboard"
                control={<Radio size="small" disabled={busy} />}
                label="Tablero (fuera)"
              />
              <FormControlLabel
                value="page"
                control={<Radio size="small" disabled={busy} />}
                label="Página activa"
              />
              <FormControlLabel
                value="pool"
                control={<Radio size="small" disabled={busy} />}
                label="Solo pool"
              />
            </RadioGroup>
          </Box>

          {assetOptions.length ? (
            <Box>
              <Stack
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
                sx={{ mb: 0.75 }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={800}
                  letterSpacing={0.3}
                >
                  REFERENCIAS DEL BOARD (MÁX. 4)
                  {mode === "sketch" || engine === "flux"
                    ? " — obligatorias"
                    : ""}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {refIds.size}/4
                  {selectedAssetIds?.length ? " · desde selección" : ""}
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  maxHeight: 260,
                  overflow: "auto",
                  p: 1,
                  borderRadius: 2,
                  bgcolor: alpha(accent, 0.04),
                  border: "1px solid",
                  borderColor: alpha(accent, 0.12),
                }}
              >
                {assetOptions.map((a) => {
                  const id = Number(a.id);
                  const checked = refIds.has(id);
                  return (
                    <RefThumb
                      key={id}
                      assetId={id}
                      title={a.Title || `#${id}`}
                      checked={checked}
                      disabled={busy || (!checked && refIds.size >= 4)}
                      fetchBlob={fetchBlob}
                      onToggle={() => toggleRef(id)}
                    />
                  );
                })}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Selecciona piezas en el board (o súbelas al pool) para usarlas
              como referencia.
            </Typography>
          )}

          {busy ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Generando… puede tardar un minuto.
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ fontWeight: 700, color: accent }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() =>
            onGenerate?.({
              mode,
              prompt: String(prompt).trim(),
              engine,
              place,
              sketchView,
              refAssetIds: [...refIds],
            })
          }
          startIcon={
            busy ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AutoAwesomeOutlinedIcon />
            )
          }
          sx={pagePrimaryButtonSx}
        >
          Generar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
